//@ts-check
import {allPass, curry, isNil, isEmpty, complement, not} from 'ramda';
import {set, get, modify, collect, remove} from 'partial.lenses';
import {string} from 'prop-types';
import {pipeAll} from '@sinclair-digital/sbg-util/dist/es/function';

export const ValidatedField = {
  value: 'value',
  errorMessage: 'errorMessage',
};

export default ValidatedField;

export const validatorShape = type => ({
  value: type,
  errorMessage: string,
});

// clearErrorMessage :: (Lens,  a (ValidatedField b)) -> a (ValidatedField b)
export const clearErrorMessage = curry((lens, state) => remove([lens, ValidatedField.errorMessage], state));

// setValidatedField :: (Lens, c, b (ValidatedField a)) -> b (ValidatedField c)
export const setValidatedField = curry((lens, value, state) => set(lens, {value, errorMessage: undefined}, state));

/**
 * @param {string} str
 * @returns {boolean}
 */
const notWhitespace = str => typeof str === 'string' ? !!str.trim().length : true;

const required = allPass([complement(isNil), complement(isEmpty), notWhitespace]);
const requiredErrorMessage = 'required';

export const validateRequiredField = lens => modify(lens, (validatorObj) => {
  const isValid = required(validatorObj.value);
  const errorMessage = isValid ? undefined : requiredErrorMessage;
  return validatorObj.errorMessage === errorMessage ? validatorObj : {...validatorObj, errorMessage};
});

export const validateRequiredMaybe = lens => modify(lens, (validatorObj) => {
  const isValid = validatorObj.value.isJust;
  const errorMessage = isValid ? undefined : requiredErrorMessage;
  return validatorObj.errorMessage === errorMessage ? validatorObj : {...validatorObj, errorMessage};
});

export const validateRequiredFields = lenses => pipeAll(lenses.map(validateRequiredField));

export const hasError = (lens, state) => not(isEmpty(collect([lens, ValidatedField.errorMessage], state)));

export const hasErrors = lenses => state => lenses.some(lens => hasError(lens, state));

export const countErrors = lenses => state =>
  lenses.reduce((count, lens) => count + (hasError(lens, state) ? 1 : 0), 0);

/**
 * @param {Array} validatedFields
 * @returns {function(object): object}
 */
export const extractValidatedFields = validatedFields => state => {
  const extractField = lens => modify(lens, get(ValidatedField.value));
  return pipeAll(validatedFields.map(extractField))(state);
};

// initValidatedField :: Lens s a -> s a -> s (ValidatedField a)
export const initValidatedField = lens => modify(lens, value => ({
  value,
  errorMessage: undefined,
}));

export const initValidatedFields = validatorFields => pipeAll(validatorFields.map(initValidatedField));
